import type { ReactElement } from 'react';
import { MaterialIcon } from './MaterialIcon.js';

/**
 * Floating action bar for mobile multi-block selection. Appears anchored to the bottom of the screen
 * (thumb-reachable) while a block selection is active — entered by long-pressing a bullet, then tapping
 * more siblings to extend the range. Drives the exact same group operations as the desktop keyboard
 * shortcuts (indent/outdent/number/delete), so there's one engine behind both. Visibility + the
 * coarse-pointer gate live in CSS (`.lapo-block-actionbar`); the host renders this only when active.
 *
 * Buttons preventDefault on pointer-down so tapping one never blurs/re-focuses the editor mid-action —
 * the selection lives in plugin state (not the DOM caret), so it survives without editor focus.
 */
function Action({
  name,
  label,
  onClick,
  active,
  danger,
  disabled,
}: {
  name: string;
  label: string;
  onClick: () => void;
  /** Reads as toggled-on (e.g. the numbered-list button when the selection is already numbered). */
  active?: boolean;
  /** Destructive styling (delete). */
  danger?: boolean;
  /** Greyed + non-interactive (e.g. Move up at the top of the document). */
  disabled?: boolean;
}): ReactElement {
  return (
    <button
      type="button"
      className={`lapo-block-action${active ? ' lapo-block-action-on' : ''}${danger ? ' lapo-block-action-danger' : ''}${disabled ? ' lapo-block-action-off' : ''}`}
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      onTouchStart={(e) => e.preventDefault()}
    >
      <MaterialIcon name={name} size={22} />
    </button>
  );
}

export interface BlockActionBarProps {
  /** Move the selected block(s) one visible row up / down through the outline. */
  onMoveUp: () => void;
  onMoveDown: () => void;
  /** Whether a move in each direction is possible (false → the button greys out at a doc edge). */
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** Outdent / indent every selected block as a group. */
  onOutdent: () => void;
  onIndent: () => void;
  /** Toggle the selected blocks between bullets and a numbered list. */
  onToggleNumbered: () => void;
  /** Whether the selection is currently numbered (so the button reads as on). */
  numbered: boolean;
  /** Copy the selected blocks to the clipboard as markdown. */
  onCopy: () => void;
  /** Delete the selected blocks. */
  onDelete: () => void;
  /** Leave selection mode (return to the text caret). */
  onDone: () => void;
}

export function BlockActionBar({
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  onOutdent,
  onIndent,
  onToggleNumbered,
  numbered,
  onCopy,
  onDelete,
  onDone,
}: BlockActionBarProps): ReactElement {
  return (
    <div className="lapo-block-actionbar" role="toolbar" aria-label="Block actions" data-testid="block-actionbar">
      <Action name="arrow_upward" label="Move blocks up" onClick={onMoveUp} disabled={!canMoveUp} />
      <Action name="arrow_downward" label="Move blocks down" onClick={onMoveDown} disabled={!canMoveDown} />
      <span className="lapo-block-action-sep" aria-hidden />
      <Action name="format_indent_decrease" label="Outdent blocks" onClick={onOutdent} />
      <Action name="format_indent_increase" label="Indent blocks" onClick={onIndent} />
      <Action
        name="format_list_numbered"
        label={numbered ? 'Make bulleted' : 'Make numbered'}
        onClick={onToggleNumbered}
        active={numbered}
      />
      <Action name="content_copy" label="Copy blocks" onClick={onCopy} />
      <Action name="delete" label="Delete blocks" onClick={onDelete} danger />
      <span className="lapo-block-action-sep" aria-hidden />
      <Action name="close" label="Done" onClick={onDone} />
    </div>
  );
}
